/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { CommandBarButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { IColumn } from '@fluentui/react/lib/DetailsList';
import { Icon } from '@fluentui/react/lib/Icon';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import orderBy from 'lodash.orderby';
import { observer, useLocalStore } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ResourcesTable } from 'src/appShell/globalHeader/globalHeaderBlades/settingsBlade/ResourcesTable';
import { GlobalHeaderLinks } from 'src/appShell/globalHeader/utils/globalHeaderLinks';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisDebouncedCollapsableSearchBox } from 'src/core/components/LuisDebouncedCollapsableSearchBox';
import { LuisText } from 'src/core/components/LuisText';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { SortKeyType } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSelection } from 'src/core/hooks/useSelection';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { isAuthoringResource } from 'src/core/utils/csAccountUtils';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';
import { createResourceStoreHouse } from 'src/luis/core/stores/resourceStoreHouse';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';
import { AzureResourcesManager } from 'src/modules/azure/components/AzureResourcesManager';
import { AzureResourcesStrategies } from 'src/modules/azure/utils/resourcesStrategies';

const messages = defineMessages({
    searchPlaceholder: { id: 'languageStudio.resourcesSettings.searchPlaceholder', defaultMessage: 'Search' },
    switchResource: { id: 'languageStudio.resourcesSettings.switchResource', defaultMessage: 'Switch resource' },
    learnMore: { id: 'languageStudio.resourcesSettings.learnMore', defaultMessage: 'Learn more about Azure resources.' },
    createNewResource: { id: 'languageStudio.resourcesSettings.createNewResource', defaultMessage: 'Create a new resource' },
    noResourceSelected: { id: 'languageStudio.resourcesSettings.noResourceSelected', defaultMessage: 'No resource selected' },
    viewInAzure: { id: 'languageStudio.resourcesSettings.viewInAzure', defaultMessage: 'View all properties in Azure Portal' },
    currentResourceLabel: { id: 'languageStudio.resourcesSettings.currentResourceLabel', defaultMessage: 'Current Language resource:' },
    noSubscriptionSelected: { id: 'languageStudio.resourcesSettings.noSubscriptionSelected', defaultMessage: 'No subscription selected' },
    currentSubscriptionLabel: { id: 'languageStudio.resourcesSettings.currentSubscriptionLabel', defaultMessage: 'Current Subscription:' },
    description: {
        id: 'languageStudio.resourcesSettings.description',
        defaultMessage: `In order to sign in to the Language Studio and use the Language Service, you need to have a Language Azure resource.
        A Language resource's endpoint and key are used to query prebuilt features and author custom models.`
    },
    noResourcesPlaceholder: {
        id: 'languageStudio.resourcesSettings.noResourcesPlaceholder',
        defaultMessage: 'No available resources yet, make sure to select subscription first.'
    }
});

const containerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({ root: { flex: 1, minHeight: 0 } });
const tableContainerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { flex: 1, minHeight: 0, marginTop: '0 !important' }
});

const sortNormalizer = (key: keyof AzureCsAccountStore) => {
    if (key === 'sku') {
        return (item: AzureCsAccountStore) => item.sku.code;
    }

    return createKeyedStringNormalizer<AzureCsAccountStore>(key);
};

export const ResourcesSettings = observer(() => {
    const { localize } = useLocalization();
    const [query, setQuery] = React.useState('');
    const [isCreateCsAccountModalHidden, setIsCreateCsAccountModalHidden] = React.useState(true);
    const [sortKey, setSortKey] = React.useState<SortKeyType<AzureCsAccountStore>>({ key: 'name', order: 'asc' });

    const switchResourceTracker = useProgressTracker();
    const { dispatcher, rootStore, uiStore } = useGlobalState();
    const { selection, selectedItems } = useSelection<AzureCsAccountStore>();
    const resourcesStoreHouse = useLocalStore<ResourceStoreHouse<AzureCsAccountStore>>(createResourceStoreHouse);
    const filteredResources = orderBy(
        substringSearchStrategy(resourcesStoreHouse.items, query, 'name'),
        [sortNormalizer(sortKey.key)],
        [sortKey.order]
    );

    const { isSignedIn } = rootStore.authStore;
    const azureSession = rootStore.azureStore.session;
    const isLanguageStudio = uiStore.portal.kind === 'languageStudio';
    const currentSubscription = rootStore.azureStore.subscriptionStoreHouse.findById(azureSession.subscriptionId);
    const isSwitchDisabled =
        !selectedItems.length || selectedItems[0]?.id === azureSession.csAccountStore?.id || switchResourceTracker.isRunning;

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canChangeAppSettingsAndDetails } = mapUserRoleToUserActions(userRole);

    const commandBarItems: ICommandBarItemProps[] = [
        {
            key: 'searchBox',
            onRender: () => (
                <LuisDebouncedCollapsableSearchBox
                    size={24}
                    value={query}
                    role="menuitem"
                    alwaysExpanded
                    debounceTime={200}
                    styles={{ root: { marginRight: '24px' } }}
                    ariaLabel={localize(messages.searchPlaceholder)}
                    placeholder={localize(messages.searchPlaceholder)}
                    onChange={(_, newValue?: string) => setQuery(newValue)}
                />
            )
        },
        {
            key: 'create',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    displayedUserRole={displayedUserRole}
                    data-automation-id="createResourceButtonToolTip"
                    hidden={canChangeAppSettingsAndDetails || item.disabled}
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            role="menuitem"
                            iconProps={{ iconName: 'Add' }}
                            data-automation-id="createResourceButton"
                            name={localize(messages.createNewResource)}
                            text={localize(messages.createNewResource)}
                            ariaLabel={localize(messages.createNewResource)}
                            onClick={() => setIsCreateCsAccountModalHidden(false)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            disabled={!isSignedIn || !canChangeAppSettingsAndDetails || item.disabled}
                        />
                    }
                />
            )
        }
    ];

    React.useEffect(() => {
        if (azureSession.subscriptionId) {
            dispatcher.dispatch('getAzureCsAccountsBySubscriptionId', {
                id: azureSession.subscriptionId,
                localStoreHouse: resourcesStoreHouse,
                filterPredicate: isLanguageStudio ? AzureResourcesStrategies.languageStudioResourceStrategy : isAuthoringResource
            });
        }
    }, [azureSession.subscriptionId]);

    const switchResource = (csAccountStore: AzureCsAccountStore) =>
        dispatcher.dispatch('switchAzureCsAccountInSession', { csAccountStore, tracker: switchResourceTracker });

    const handleSortChange = (column: IColumn) => {
        setSortKey({ key: column.key as keyof AzureCsAccountStore, order: column.isSortedDescending ? 'asc' : 'desc' });
        selection.setAllSelected(false);
    };

    return (
        <Stack styles={containerStyles} tokens={{ childrenGap: 18 }}>
            <LuisText>
                {localize(messages.description)}
                &nbsp;
                <ExternalLink href="https://aka.ms/languageresources">{localize(messages.learnMore)}</ExternalLink>
            </LuisText>

            <Stack tokens={{ childrenGap: 5 }}>
                <LuisText>
                    <LuisText variant="mediumPlus" fontWeight={600}>
                        {localize(messages.currentSubscriptionLabel)}
                    </LuisText>
                    &nbsp;
                    <LuisText variant="mediumPlus">{currentSubscription?.name ?? localize(messages.noSubscriptionSelected)}</LuisText>
                </LuisText>
                <LuisText>
                    <LuisText variant="mediumPlus" fontWeight={600}>
                        {localize(messages.currentResourceLabel)}
                    </LuisText>
                    &nbsp;
                    <LuisText variant="mediumPlus">{azureSession.csAccountStore?.name ?? localize(messages.noResourceSelected)}</LuisText>
                </LuisText>
            </Stack>

            <LuisCommandBar items={commandBarItems} />

            <Stack styles={tableContainerStyles}>
                <ResourcesTable
                    sortKey={sortKey}
                    selection={selection}
                    items={filteredResources}
                    onShouldVirtualize={() => false}
                    areItemsFiltered={Boolean(query)}
                    overallItemsCount={filteredResources.length}
                    onSortColumn={handleSortChange}
                    isLoading={resourcesStoreHouse.tracker.isRunning}
                    emptyPlaceholder={localize(messages.noResourcesPlaceholder)}
                />
            </Stack>

            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 38 }}>
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                    <PrimaryButton
                        disabled={isSwitchDisabled}
                        ariaLabel={localize(messages.switchResource)}
                        onClick={() => switchResource(selectedItems[0])}>
                        {localize(messages.switchResource)}
                    </PrimaryButton>
                    {switchResourceTracker.isRunning && <Spinner size={SpinnerSize.small} />}
                </Stack>
                <ExternalLink href={GlobalHeaderLinks.loggedInHeaderViewAzureLink}>
                    <Stack horizontal verticalAlign="center">
                        <LuisText>{localize(messages.viewInAzure)}</LuisText>
                        &nbsp;
                        <Icon iconName="MiniExpand" />
                    </Stack>
                </ExternalLink>
            </Stack>

            {!isCreateCsAccountModalHidden && (
                <AzureResourcesManager
                    isCreateResourceVisible={!isCreateCsAccountModalHidden}
                    serviceTarget={isLanguageStudio ? 'TextAnalytics' : 'LUIS.Authoring'}
                    onChangeCreateResourceVisible={isVisible => setIsCreateCsAccountModalHidden(!isVisible)}
                />
            )}
        </Stack>
    );
});
